'use client';

import { type MfaMessageDto } from '@terra/shared-contracts-applications';
import type {
  BodySignatureConfiguration,
  CredentialVerification,
} from '@terra/shared-schemas-orchestrator';
import { MfaProviderSchema } from '@terra/shared-schemas-orchestrator';
import type { AuthMaterialExpiry } from '@terra/shared-contracts-authenticator';
import { Box, Divider, HStack, VStack } from '@terra/ui-styled-system';
import {
  Tick01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  ArrowUpDownIcon,
  InboxIcon,
  InformationCircleIcon,
  Key01Icon,
  Loading03Icon,
  PlayIcon,
  Add01Icon,
  SquareIcon,
  Delete01Icon,
  Tv01Icon,
  UserIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isEqual } from 'radash';
import type { SseSubscribe } from '@terra/shared-sse-contracts';
import {
  isCredentialVerificationCompleted,
  isCredentialVerificationInProgress,
} from '@terra/shared-utils';
import { Button } from '../../components/button';
import { Collapsible } from '../../components/collapsible';
import { Field } from '../../components/field';
import { IconButton } from '../../components/icon-button';
import { Input } from '../../components/input';
import { createSelectListCollection, Select } from '../../components/select';
import { Switch } from '../../components/switch';
import { Text } from '../../components/text';
import { Tooltip } from '../../components/tooltip';
import { AiBrowserStreamingFrame } from '../chat/components/AiBrowserStreamingFrame';
import { ConfirmationDialog } from '../confirmation-dialog';
import { SensitiveInput } from '../inputs/SensitiveInput';
import { type GatheredArtifactItem } from './GatheredArtifactsPanel';
import { CredentialVerifiedBadge } from './CredentialVerifiedBadge';
import { FeatureAnalysisCredentialBadge } from './FeatureAnalysisCredentialBadge';
import {
  CredentialInboxMessagesPanel,
  credentialHasResolvableMailbox,
  resolveGeneratedMailboxAddress,
  type FetchCredentialInboxFn,
} from './CredentialInboxMessagesPanel';
import { GeneratedMailboxSection } from './GeneratedMailboxSection';
import {
  MfaConfiguration as MfaConfigComponent,
  type MfaProvider,
} from '../mfa';
import { toasterApi } from '../toast/ToastProvider';
import { RoleSelectionDropdown } from '../application-roles/RoleSelectionDropdown';
import { resolveCredentialRoleLabel } from './resolve-credential-role-label';

export { FeatureAnalysisCredentialBadge } from './FeatureAnalysisCredentialBadge';

const EMPTY_STREAMING_ID_BY_CREDENTIAL: Record<string, string> = {};
const DEFAULT_SECRET_VALUE = '*************************************';
const CREDENTIAL_ROW_GRID_TEMPLATE_COLUMNS =
  'minmax(0, 1fr) max-content max-content';

type CredentialFieldErrors = {
  role?: string;
  username?: string;
  loginUrl?: string;
  authentication?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  mtlsCert?: string;
  mtlsKey?: string;
  mtlsOrigin?: string;
  headers?: string[];
  cookies?: string[];
  secrets?: string[];
  bodySignature?: string;
  mfaTotpSecret?: string;
};
type ApplicationCredential = {
  id: string;
  ssoProvider: string;
  username: string;
  role: string;
  roleId?: string | null;
  roleDescription?: string | null;
  loginUrl?: string | null;
  password?: string;
  basicAuthUsername?: string | null;
  basicAuthPassword?: string | null;
  mtlsCert?: string | null;
  mtlsKey?: string | null;
  mtlsPfx?: string | null;
  mtlsOrigin?: string | null;
  mtlsPassphrase?: string | null;
  tenant?: string;
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string }>;
  secrets: Array<{ key: string; value: string }>;
  bodySignature?: BodySignatureConfiguration | null;
  verification?: CredentialVerification & {
    authMaterialExpiry?: AuthMaterialExpiry | null;
    artifacts?: GatheredArtifactItem[];
  };
  mfaProviders?: MfaProvider[] | null;
  generatedBy?: 'USER' | 'FEATURE_ANALYSIS' | null;
  generatedMailboxAddress?: string | null;
  isDisabled?: boolean;
  disabledAt?: Date | null;
  disabledBy?: string | null;
  // Role data when linked to custom role
  applicationRole?: {
    loginUrl?: string | null;
    description?: string | null;
  } | null;
};

export interface ApplicationCredentialsProps {
  applicationId?: string;
  applicationUrl?: string;
  credentials: ApplicationCredential[];
  onCredentialsChange: (credentials: ApplicationCredential[]) => void;
  title?: string;
  description?: string;
  allowViewingCredentials?: boolean;
  onCopy?: (value: string) => Promise<void>;
  intent: 'create' | 'update' | 'view';
  customRoles?: import('@terra/shared-contracts-applications').ApplicationRoleDto[];
  onCreateRole?: () => void;
  onFetchMfaMessages?: (params: {
    credentialId: string;
    mfaConfigId: string;
  }) => Promise<{
    success: boolean;
    messages?: MfaMessageDto[];
    error?: string;
  }>;
  onFetchCredentialInbox?: FetchCredentialInboxFn;
  onFetchMfaWebhookConfig?: (params: {
    credentialId: string;
    mfaConfigId: string;
  }) => Promise<{
    success: boolean;
    endpointPath?: string;
    signatureHeader?: string;
    timestampHeader?: string;
    deliveryIdHeader?: string;
    secret?: string;
    secretLast4?: string;
    error?: string;
  }>;
  onTestCredential?: (
    credentialId: string,
    streamingId: string,
  ) => Promise<void>;
  onAbortVerification?: (credentialId: string) => Promise<void>;
  onToggleCredentialStatus?: (
    credentialId: string,
    isDisabled: boolean,
  ) => Promise<void>;
  onGenerateMailbox?: () => Promise<{
    success: boolean;
    emailAddress?: string;
    error?: string;
  }>;
  onSSESubscribe?: SseSubscribe;
  persistedStreamingIds?: Record<string, string>;
  onStreamingIdsChange?: (streamingIds: Record<string, string>) => void;
  showSessionTooltip?: boolean;
}
export type ApplicationCredentialsHandle = {
  hasErrors: () => boolean;
  isDirty: () => boolean;
  reset: () => void;
  validate: () => boolean;
};
const isDefaultSecretValue = (value?: string): boolean => {
  return value === DEFAULT_SECRET_VALUE;
};

const hasStoredSecretValue = (value?: string | null): boolean => {
  return (value?.trim().length ?? 0) > 0;
};

const getCredentialVerificationStartErrorMessage = ({
  error,
}: {
  error: unknown;
}): string => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return 'Failed to start credential verification';
};

const getBasicAuthCredentials = ({
  credential,
}: {
  credential: ApplicationCredential;
}): { username: string; password: string } => {
  const username = credential.basicAuthUsername ?? '';
  const password = credential.basicAuthPassword ?? '';

  return { username, password };
};

const getMutualTlsConfig = ({
  credential,
}: {
  credential: ApplicationCredential;
}): {
  cert: string;
  key: string;
  pfx: string;
  origin: string;
  passphrase: string;
} => {
  return {
    cert: credential.mtlsCert ?? '',
    key: credential.mtlsKey ?? '',
    pfx: credential.mtlsPfx ?? '',
    origin: credential.mtlsOrigin ?? '',
    passphrase: credential.mtlsPassphrase ?? '',
  };
};

const isPkcs12File = ({ fileName }: { fileName: string }): boolean => {
  return /\.(p12|pfx)$/i.test(fileName);
};

const arrayBufferToBase64 = ({ buffer }: { buffer: ArrayBuffer }): string => {
  let binary = '';
  const bytes = new Uint8Array(buffer);

  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
};

const getOriginFromApplicationUrl = ({
  applicationUrl,
}: {
  applicationUrl?: string;
}): string => {
  const normalizedApplicationUrl = applicationUrl?.trim();

  if (!normalizedApplicationUrl) {
    return '';
  }

  try {
    return new URL(normalizedApplicationUrl).origin;
  } catch {
    return '';
  }
};

const applyMutualTlsFileChange = ({
  credential,
  field,
  value,
  valueType,
  applicationUrl,
}: {
  credential: ApplicationCredential;
  field: 'mtlsCert' | 'mtlsKey';
  value: string;
  valueType?: 'pem' | 'pfx';
  applicationUrl?: string;
}): ApplicationCredential => {
  const defaultOrigin = getOriginFromApplicationUrl({ applicationUrl });
  const currentOrigin = credential.mtlsOrigin?.trim() ?? '';

  if (field === 'mtlsCert' && valueType === 'pfx') {
    return {
      ...credential,
      mtlsCert: '',
      mtlsKey: '',
      mtlsPfx: value,
      ...(currentOrigin.length === 0 && defaultOrigin.length > 0
        ? { mtlsOrigin: defaultOrigin }
        : {}),
    };
  }

  return {
    ...credential,
    [field]: value,
    ...(field === 'mtlsCert' || field === 'mtlsKey'
      ? {
          mtlsPfx: '',
          mtlsPassphrase: '',
        }
      : {}),
    ...(currentOrigin.length === 0 && defaultOrigin.length > 0
      ? { mtlsOrigin: defaultOrigin }
      : {}),
  };
};

const createDefaultCredential = (): ApplicationCredential => {
  return {
    id: '',
    ssoProvider: 'NONE',
    username: '',
    role: 'admin',
    password: '',
    basicAuthUsername: '',
    basicAuthPassword: '',
    mtlsCert: '',
    mtlsKey: '',
    mtlsPfx: '',
    mtlsOrigin: '',
    mtlsPassphrase: '',
    tenant: '',
    headers: [],
    cookies: [],
    secrets: [],
    bodySignature: null,
    mfaProviders: [],
    generatedMailboxAddress: null,
  };
};

export const ssoProviderOptions = createSelectListCollection({
  items: [
    {
      label: 'None',
      value: 'NONE',
    },
    {
      label: 'Google',
      value: 'GOOGLE',
    },
    {
      label: 'Microsoft',
      value: 'MICROSOFT',
    },
    {
      label: 'GitHub',
      value: 'GITHUB',
    },
    {
      label: 'GitLab',
      value: 'GITLAB',
    },
    {
      label: 'Other',
      value: 'OTHER',
    },
  ],
});
export const roleOptions = createSelectListCollection({
  items: [
    {
      label: 'Admin',
      value: 'admin',
    },
    {
      label: 'User',
      value: 'user',
    },
    {
      label: 'Guest',
      value: 'guest',
    },
    {
      label: 'Developer',
      value: 'developer',
    },
    {
      label: 'Tester',
      value: 'tester',
    },
    {
      label: 'Manager',
      value: 'manager',
    },
    {
      label: 'Viewer',
      value: 'viewer',
    },
    {
      label: 'Editor',
      value: 'editor',
    },
    {
      label: 'Other',
      value: 'OTHER',
    },
  ],
});
export const bodySignatureAlgorithmOptions = createSelectListCollection({
  items: [
    { label: 'SHA-1', value: 'sha1' },
    { label: 'SHA-256', value: 'sha256' },
    { label: 'SHA-512', value: 'sha512' },
  ],
});
export const ApplicationCredentials = forwardRef<
  ApplicationCredentialsHandle,
  ApplicationCredentialsProps
>(
  (
    {
      credentials,
      onCredentialsChange,
      applicationId,
      applicationUrl,
      title,
      description,
      allowViewingCredentials = false,
      onCopy,
      intent,
      onFetchMfaMessages,
      onFetchCredentialInbox,
      onFetchMfaWebhookConfig,
      onTestCredential,
      onAbortVerification,
      onToggleCredentialStatus,
      onGenerateMailbox,
      onSSESubscribe,
      persistedStreamingIds,
      onStreamingIdsChange,
      showSessionTooltip = false,
      customRoles: availableCustomRoles = [],
      onCreateRole,
    },
    ref,
  ) => {
    const t = useTranslations('designSystem.applicationCredentials');
    const scrollRef = useRef<HTMLDivElement>(null);
    const mtlsFileInputRefs = useRef<Record<string, HTMLInputElement | null>>(
      {},
    );
    const [visibleCredentials, setVisibleCredentials] = useState<
      Record<string, boolean>
    >({});
    const [copiedCredentials, setCopiedCredentials] = useState<
      Record<string, boolean>
    >({});
    const [errors, setErrors] = useState<CredentialFieldErrors[]>([]);
    const [isDirtyState, setIsDirtyState] = useState(false);
    const [mailboxGeneratingIndex, setMailboxGeneratingIndex] = useState<
      number | null
    >(null);
    const [mailboxSuccessIndex, setMailboxSuccessIndex] = useState<
      number | null
    >(null);
    const [requiresCredentials, setRequiresCredentials] = useState(true);
    const [openModalCredentialId, setOpenModalCredentialId] = useState<
      string | null
    >(null);
    const [customSsoProviders, setCustomSsoProviders] = useState<
      Record<number, boolean>
    >({});
    const [customRoleInputs, setCustomRoleInputs] = useState<
      Record<number, boolean>
    >({});
    const [collapsedCredentials, setCollapsedCredentials] = useState<
      Record<number, boolean>
    >({});
    const [collapsedSections, setCollapsedSections] = useState<
      Record<number, Record<string, boolean>>
    >({});
    const [testingById, setTestingById] = useState<Record<string, boolean>>({});
    const isSectionCollapsed = (
      credentialIndex: number,
      section: string,
    ): boolean => {
      return collapsedSections[credentialIndex]?.[section] ?? false;
    };

    const [streamingIdById, setStreamingIdById] = useState<
      Record<string, string>
    >(persistedStreamingIds ?? EMPTY_STREAMING_ID_BY_CREDENTIAL);
    const [credentialToToggle, setCredentialToToggle] = useState<{
      id: string;
      currentStatus: boolean;
    } | null>(null);
    const [isTogglingStatus, setIsTogglingStatus] = useState(false);
    const sanitizeCredentialsForOutput = useCallback(
      (nextCredentials: ApplicationCredential[]): ApplicationCredential[] => {
        return nextCredentials.map((credential) => ({
          ...credential,
          secrets: credential.secrets,
        }));
      },
      [],
    );
    const emitCredentialsChange = useCallback(
      (nextCredentials: ApplicationCredential[]) => {
        onCredentialsChange(sanitizeCredentialsForOutput(nextCredentials));
      },
      [onCredentialsChange, sanitizeCredentialsForOutput],
    );

    // Only when the tab store updates: pull into local state. Do not depend on
    // streamingIdById here — that would reset local to stale persisted right
    // after handleTestCredential updates local but before the parent persists.
    // Skip setState when deep-equal to avoid render loops (ErrorBoundary).
    useEffect(() => {
      const next = persistedStreamingIds ?? EMPTY_STREAMING_ID_BY_CREDENTIAL;

      setStreamingIdById((prev) => (isEqual(prev, next) ? prev : next));
    }, [persistedStreamingIds]);
    useEffect(() => {
      const persisted =
        persistedStreamingIds ?? EMPTY_STREAMING_ID_BY_CREDENTIAL;

      if (!onStreamingIdsChange || isEqual(streamingIdById, persisted)) {
        return;
      }

      onStreamingIdsChange(streamingIdById);
    }, [onStreamingIdsChange, persistedStreamingIds, streamingIdById]);
    useEffect(() => {
      if (intent === 'update') {
        setRequiresCredentials(credentials.length > 0);

        return;
      }

      if (credentials.length === 0 && requiresCredentials) {
        emitCredentialsChange([createDefaultCredential()]);
      }
    }, [
      intent,
      credentials.length,
      requiresCredentials,
      emitCredentialsChange,
    ]);
    useEffect(() => {
      const predefinedRoles = roleOptions.items.map((item) => item.value);
      const predefinedSsoProviders = ssoProviderOptions.items.map(
        (item) => item.value,
      );
      const newCustomRoleInputs: Record<number, boolean> = {};
      const newCustomSsoProviders: Record<number, boolean> = {};

      credentials.forEach((credential, index) => {
        // Check if role is a custom role (not in predefined and not linked to a custom role)
        if (
          !predefinedRoles.includes(credential.role) &&
          !credential.roleId &&
          !availableCustomRoles.find((r) => r.name === credential.role)
        ) {
          newCustomRoleInputs[index] = true;
        }

        if (!predefinedSsoProviders.includes(credential.ssoProvider)) {
          newCustomSsoProviders[index] = true;
        }
      });
      // Only update state if values have actually changed
      setCustomRoleInputs((prev) =>
        isEqual(prev, newCustomRoleInputs) ? prev : newCustomRoleInputs,
      );
      setCustomSsoProviders((prev) =>
        isEqual(prev, newCustomSsoProviders) ? prev : newCustomSsoProviders,
      );
    }, [credentials, availableCustomRoles]);
    // Initialize collapsed state for new credentials only, preserving user-set states
    useEffect(() => {
      setCollapsedCredentials((prev) => {
        const newCollapsed = { ...prev };

        credentials.forEach((_, index) => {
          // Only set collapsed state if it doesn't exist yet
          if (prev[index] === undefined) {
            newCollapsed[index] = true;
          }
        });

        return newCollapsed;
      });
    }, [credentials]);
    const handleRequiresCredentialsToggle = (details: { checked: boolean }) => {
      setRequiresCredentials(details.checked);
      if (details.checked && credentials.length === 0) {
        emitCredentialsChange([createDefaultCredential()]);
      } else if (!details.checked) {
        emitCredentialsChange([]);
      }
    };

    const handleSsoProviderChange = (index: number, selectedValue: string) => {
      if (selectedValue === 'OTHER') {
        setCustomSsoProviders((prev) => ({ ...prev, [index]: true }));
        handleCredentialStringChange(index, 'ssoProvider', '');
      } else {
        setCustomSsoProviders((prev) => ({ ...prev, [index]: false }));
        handleCredentialStringChange(index, 'ssoProvider', selectedValue);
      }
    };

    const handleRoleChange = (
      index: number,
      selectedValue: string,
      roleId?: string | null,
    ) => {
      if (selectedValue === 'OTHER') {
        setCustomRoleInputs((prev) => ({ ...prev, [index]: true }));
        // Clear roleId when selecting OTHER
        const newCredentials = [...credentials];
        const currentRoleDescription = newCredentials[index].roleDescription;

        newCredentials[index] = {
          ...newCredentials[index],
          role: '',
          roleId: null,
          applicationRole: null,
          roleDescription: currentRoleDescription,
        };
        emitCredentialsChange(newCredentials);
      } else {
        setCustomRoleInputs((prev) => ({ ...prev, [index]: false }));
        const newCredentials = [...credentials];
        const selectedCustomRole =
          roleId && availableCustomRoles.length > 0
            ? availableCustomRoles.find((r) => r.id === roleId)
            : null;
        const previousRoleDescription = newCredentials[index].roleDescription;

        newCredentials[index] = {
          ...newCredentials[index],
          role: selectedValue,
          roleId: roleId || null,
          applicationRole: selectedCustomRole
            ? {
                description: selectedCustomRole.description || null,
              }
            : null,
          roleDescription: selectedCustomRole
            ? selectedCustomRole.description || null
            : previousRoleDescription || null,
        };
        emitCredentialsChange(newCredentials);
      }
    };

    const isValidUrl = (url: string | null | undefined): boolean => {
      if (!url || url.trim() === '') {
        return true;
      }

      try {
        new URL(url);

        return true;
      } catch {
        return false;
      }
    };

    const validateCredential = (
      credential: ApplicationCredential,
    ): CredentialFieldErrors => {
      const credentialErrors: CredentialFieldErrors = {};

      if (!credential.role?.trim()) {
        credentialErrors.role = t('errors.roleRequired');
      }

      if (credential.loginUrl && !isValidUrl(credential.loginUrl)) {
        credentialErrors.loginUrl = t('errors.invalidLoginUrl');
      }

      const basicAuth = getBasicAuthCredentials({ credential });
      const hasBasicAuth =
        basicAuth.username.trim().length > 0 ||
        basicAuth.password.trim().length > 0;
      const hasCompleteBasicAuth =
        basicAuth.username.trim().length > 0 &&
        basicAuth.password.trim().length > 0;
      const mtlsConfig = getMutualTlsConfig({ credential });
      const hasAnyMutualTlsField =
        mtlsConfig.cert.trim().length > 0 ||
        mtlsConfig.key.trim().length > 0 ||
        mtlsConfig.pfx.trim().length > 0 ||
        mtlsConfig.origin.trim().length > 0 ||
        mtlsConfig.passphrase.trim().length > 0;
      const hasCompleteMutualTls =
        (mtlsConfig.pfx.trim().length > 0 ||
          (mtlsConfig.cert.trim().length > 0 &&
            mtlsConfig.key.trim().length > 0)) &&
        mtlsConfig.origin.trim().length > 0;

      if (hasBasicAuth && basicAuth.username.trim().length === 0) {
        credentialErrors.basicAuthUsername = t(
          'errors.basicAuthUsernameRequired',
        );
      }

      if (hasBasicAuth && basicAuth.password.trim().length === 0) {
        credentialErrors.basicAuthPassword = t(
          'errors.basicAuthPasswordRequired',
        );
      }

      const hasUsername = !!credential.username?.trim();

      if (
        hasAnyMutualTlsField &&
        mtlsConfig.pfx.trim().length === 0 &&
        mtlsConfig.cert.trim().length === 0
      ) {
        credentialErrors.mtlsCert = t('errors.mtlsCertRequired');
      }

      if (
        hasAnyMutualTlsField &&
        mtlsConfig.pfx.trim().length === 0 &&
        mtlsConfig.key.trim().length === 0
      ) {
        credentialErrors.mtlsKey = t('errors.mtlsKeyRequired');
      }

      if (hasAnyMutualTlsField && mtlsConfig.origin.trim().length === 0) {
        credentialErrors.mtlsOrigin = t('errors.mtlsOriginRequired');
      }

      const hasPassword = !!credential.password?.trim();
      const hasHeaders = credential.headers.length > 0;
      const hasCookies = credential.cookies.length > 0;
      const hasSecrets = credential.secrets.length > 0;

      if (hasPassword && !hasUsername) {
        credentialErrors.username = t('errors.usernameRequired');
      }

      if (hasHeaders) {
        const headerErrors: string[] = [];

        credential.headers.forEach((header, index) => {
          if (!header.key?.trim()) {
            headerErrors[index] = t('errors.headerKeyRequired');
          } else if (!header.value?.trim()) {
            headerErrors[index] = t('errors.headerValueRequired');
          }
        });
        if (headerErrors.length > 0) {
          credentialErrors.headers = headerErrors;
        }
      }

      if (hasCookies) {
        const cookieErrors: string[] = [];

        credential.cookies.forEach((cookie, index) => {
          if (!cookie.name?.trim()) {
            cookieErrors[index] = t('errors.cookieNameRequired');
          } else if (!cookie.value?.trim()) {
            cookieErrors[index] = t('errors.cookieValueRequired');
          }
        });
        if (cookieErrors.length > 0) {
          credentialErrors.cookies = cookieErrors;
        }
      }

      if (hasSecrets) {
        const secretErrors: string[] = [];

        credential.secrets.forEach((secret, index) => {
          if (!secret.key?.trim()) {
            secretErrors[index] = t('errors.secretKeyRequired');
          } else if (!secret.value?.trim()) {
            secretErrors[index] = t('errors.secretValueRequired');
          }
        });
        if (secretErrors.length > 0) {
          credentialErrors.secrets = secretErrors;
        }
      }

      const hasBodySignatureConfig =
        credential.bodySignature != null &&
        (credential.bodySignature.headerName.trim().length > 0 ||
          credential.bodySignature.secret.trim().length > 0);

      if (hasBodySignatureConfig) {
        const bodySignature =
          credential.bodySignature as BodySignatureConfiguration;

        if (
          !bodySignature.headerName.trim() ||
          !bodySignature.secret.trim() ||
          !bodySignature.algorithm.trim()
        ) {
          credentialErrors.bodySignature = t('errors.bodySignatureAllFields');
        }
      }

      const hasCompleteBodySignature =
        credential.bodySignature != null &&
        credential.bodySignature.headerName.trim().length > 0 &&
        credential.bodySignature.secret.trim().length > 0 &&
        credential.bodySignature.algorithm.trim().length > 0;

      if (
        !hasUsername &&
        !hasCompleteBasicAuth &&
        !hasCompleteMutualTls &&
        !hasCompleteBodySignature
      ) {
        credentialErrors.authentication = t('errors.authenticationRequired');
      }

      const otpConfig = credential.mfaProviders?.find(
        (c) => c.providerType === 'OTP' && c.enabled,
      );

      if (otpConfig && !otpConfig.totpSecret?.trim()) {
        credentialErrors.mfaTotpSecret = t('errors.totpSecretRequired');
      }

      return credentialErrors;
    };

    const hasCredentialErrors = (
      credentialErrors: CredentialFieldErrors,
    ): boolean => {
      return !!Object.values(credentialErrors).some((error) => error);
    };

    const clearAuthenticationErrorIfValid = (
      currentErrors: CredentialFieldErrors,
      credential: ApplicationCredential,
    ) => {
      const basicAuth = getBasicAuthCredentials({ credential });
      const hasCompleteBasicAuth =
        basicAuth.username.trim().length > 0 &&
        basicAuth.password.trim().length > 0;
      const mutualTls = getMutualTlsConfig({ credential });
      const hasCompleteMutualTls =
        (mutualTls.pfx.trim().length > 0 ||
          (mutualTls.cert.trim().length > 0 &&
            mutualTls.key.trim().length > 0)) &&
        mutualTls.origin.trim().length > 0;

      if (currentErrors.authentication) {
        const hasUsername = !!credential.username?.trim();
        const hasCompleteBodySignature =
          credential.bodySignature != null &&
          credential.bodySignature.headerName.trim().length > 0 &&
          credential.bodySignature.secret.trim().length > 0 &&
          credential.bodySignature.algorithm.trim().length > 0;

        if (
          hasUsername ||
          hasCompleteBasicAuth ||
          hasCompleteMutualTls ||
          hasCompleteBodySignature
        ) {
          delete currentErrors.authentication;
        }
      }
    };

    const validateAndReturnErrors = () => {
      if (!requiresCredentials) {
        return true;
      }

      const newErrors = credentials.map((credential) =>
        validateCredential(credential),
      );

      setErrors(newErrors);

      return newErrors.every(
        (credentialErrors) => !hasCredentialErrors(credentialErrors),
      );
    };

    useImperativeHandle(ref, () => ({
      hasErrors: () => {
        if (!requiresCredentials) {
          return false;
        }

        return credentials.some((credential) =>
          hasCredentialErrors(validateCredential(credential)),
        );
      },
      isDirty: () => isDirtyState,
      validate: () => {
        const isValid = validateAndReturnErrors();

        return isValid;
      },
      reset: () => {
        setIsDirtyState(false);
      },
    }));
    const toggleCredentialVisibility = (fieldKey: string) => {
      setVisibleCredentials((prev) => ({
        ...prev,
        [fieldKey]: !prev[fieldKey],
      }));
    };

    const handleCopySuccess = (fieldKey: string) => {
      setCopiedCredentials((prev) => ({ ...prev, [fieldKey]: true }));
      setTimeout(() => {
        setCopiedCredentials((prev) => ({ ...prev, [fieldKey]: false }));
      }, 2000);
    };

    const toggleCredentialCollapse = (index: number) => {
      setCollapsedCredentials((prev) => {
        const newState = {
          ...prev,
          [index]: !prev[index],
        };

        return newState;
      });
    };

    const handleAddCredential = () => {
      const newCredentials = [
        ...credentials,
        {
          ssoProvider: 'NONE',
          username: '',
          role: 'admin',
          id: '',
          basicAuthUsername: '',
          basicAuthPassword: '',
          mtlsCert: '',
          mtlsKey: '',
          mtlsPfx: '',
          mtlsOrigin: '',
          mtlsPassphrase: '',
          headers: [],
          cookies: [],
          secrets: [],
        },
      ];

      emitCredentialsChange(newCredentials);
      requestAnimationFrame(() => {
        scrollRef?.current?.scrollTo({
          top: scrollRef?.current?.scrollHeight,
          behavior: 'smooth',
        });
      });
    };

    const handleRemoveUnsavedCredential = (index: number) => {
      const credential = credentials[index];

      if (credential.id) return;
      const newCredentials = [...credentials];

      newCredentials.splice(index, 1);
      onCredentialsChange(newCredentials);
      setCustomSsoProviders((prev) => {
        const newState = { ...prev };

        delete newState[index];
        const reindexed: Record<number, boolean> = {};

        Object.keys(newState).forEach((key) => {
          const oldIndex = parseInt(key);

          if (oldIndex > index) {
            reindexed[oldIndex - 1] = newState[oldIndex];
          } else {
            reindexed[oldIndex] = newState[oldIndex];
          }
        });

        return reindexed;
      });
      setCustomRoleInputs((prev) => {
        const newState = { ...prev };

        delete newState[index];
        const reindexed: Record<number, boolean> = {};

        Object.keys(newState).forEach((key) => {
          const oldIndex = parseInt(key);

          if (oldIndex > index) {
            reindexed[oldIndex - 1] = newState[oldIndex];
          } else {
            reindexed[oldIndex] = newState[oldIndex];
          }
        });

        return reindexed;
      });
      setCollapsedCredentials((prev) => {
        const newState = { ...prev };

        delete newState[index];
        const reindexed: Record<number, boolean> = {};

        Object.keys(newState).forEach((key) => {
          const oldIndex = parseInt(key);

          if (oldIndex > index) {
            reindexed[oldIndex - 1] = newState[oldIndex];
          } else {
            reindexed[oldIndex] = newState[oldIndex];
          }
        });

        return reindexed;
      });
    };

    const clearCredentialTestingState = (credentialId: string) => {
      setTestingById((prev) => ({ ...prev, [credentialId]: false }));
      setStreamingIdById((prev) => {
        const next = { ...prev };

        delete next[credentialId];

        return next;
      });
    };

    const handleTestCredential = async (credentialId?: string) => {
      if (!credentialId || !onTestCredential) return;
      const streamingId = uuidv4();

      setStreamingIdById((prev) => ({ ...prev, [credentialId]: streamingId }));
      setTestingById((prev) => ({ ...prev, [credentialId]: true }));

      try {
        await onTestCredential(credentialId, streamingId);
      } catch (error) {
        clearCredentialTestingState(credentialId);
        toasterApi.error({
          title: t('verificationFailed'),
          description: getCredentialVerificationStartErrorMessage({ error }),
        });
      }
    };

    const handleAbortVerification = async (credentialId?: string) => {
      if (!credentialId || !onAbortVerification) return;

      try {
        await onAbortVerification(credentialId);
        clearCredentialTestingState(credentialId);
        toasterApi.info({
          title: t('verificationAborted'),
          description: t('verificationAbortedDescription'),
        });
      } catch (error) {
        toasterApi.error({
          title: t('verificationAbortFailed'),
          description:
            error instanceof Error
              ? error.message
              : t('verificationAbortFailed'),
        });
      }
    };

    useEffect(() => {
      const completedCredentialIds = credentials
        .filter((credential) => {
          return (
            !!credential.id &&
            isCredentialVerificationCompleted({
              status: credential.verification?.status,
            })
          );
        })
        .map((credential) => credential.id);

      if (completedCredentialIds.length === 0) {
        return;
      }

      setTestingById((prev) => {
        const next = { ...prev };
        let hasChanges = false;

        for (const credentialId of completedCredentialIds) {
          if (next[credentialId]) {
            delete next[credentialId];
            hasChanges = true;
          }
        }

        return hasChanges ? next : prev;
      });
      setStreamingIdById((prev) => {
        const next = { ...prev };
        let hasChanges = false;

        for (const credentialId of completedCredentialIds) {
          if (next[credentialId]) {
            delete next[credentialId];
            hasChanges = true;
          }
        }

        return hasChanges ? next : prev;
      });
    }, [credentials]);
    const handleToggleCredentialStatus = (
      credentialId: string,
      currentStatus: boolean,
    ) => {
      const enabledCount = credentials.filter(
        (cred) => !cred.isDisabled && cred.id,
      ).length;

      if (!currentStatus && enabledCount === 1) {
        toasterApi.warning({
          title: t('toggleStatus.lastCredentialWarningTitle'),
          description: t('toggleStatus.lastCredentialWarningDescription'),
        });
      }

      setCredentialToToggle({ id: credentialId, currentStatus });
    };

    const confirmToggleCredentialStatus = async () => {
      if (!credentialToToggle || !onToggleCredentialStatus) return;
      setIsTogglingStatus(true);

      try {
        const newDisabledStatus = !credentialToToggle.currentStatus;

        await onToggleCredentialStatus(
          credentialToToggle.id,
          newDisabledStatus,
        );
        toasterApi.success({
          title: newDisabledStatus
            ? t('toggleStatus.disabledTitle')
            : t('toggleStatus.enabledTitle'),
          description: newDisabledStatus
            ? t('toggleStatus.disabledDescription')
            : t('toggleStatus.enabledDescription'),
        });
      } catch (error) {
        toasterApi.error({
          title: t('toggleStatus.errorTitle'),
          description:
            error instanceof Error
              ? error.message
              : t('toggleStatus.errorDescription'),
        });
      } finally {
        setIsTogglingStatus(false);
        setCredentialToToggle(null);
      }
    };

    const handleCredentialStringChange = (
      index: number,
      field:
        | 'username'
        | 'role'
        | 'ssoProvider'
        | 'password'
        | 'tenant'
        | 'loginUrl'
        | 'roleDescription',
      value: string,
    ) => {
      const newCredentials = [...credentials];

      if (field === 'loginUrl') {
        const trimmedValue = value.trim();

        newCredentials[index] = {
          ...newCredentials[index],
          loginUrl: trimmedValue !== '' ? trimmedValue : null,
        };
      } else if (field === 'roleDescription') {
        newCredentials[index] = {
          ...newCredentials[index],
          roleDescription: value || null,
        };
      } else {
        newCredentials[index] = {
          ...newCredentials[index],
          [field]: value,
        };
      }

      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      if (errors[index] && Object.keys(errors[index]).length > 0) {
        const newErrors = [...errors];
        const currentErrors = { ...newErrors[index] };

        if (field === 'role' && currentErrors.role) {
          delete currentErrors.role;
        }

        if (field === 'username' && currentErrors.username) {
          delete currentErrors.username;
        }

        if (field === 'loginUrl' && currentErrors.loginUrl) {
          if (isValidUrl(value)) {
            delete currentErrors.loginUrl;
          }
        }

        if (field === 'password') {
          clearAuthenticationErrorIfValid(currentErrors, newCredentials[index]);
        }

        newErrors[index] = currentErrors;
        setErrors(newErrors);
      }
    };

    const handleMfaProvidersChange = (
      credentialIndex: number,
      providers: MfaProvider[],
    ) => {
      const newCredentials = [...credentials];

      newCredentials[credentialIndex] = {
        ...newCredentials[credentialIndex],
        mfaProviders: providers,
      };
      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      if (errors[credentialIndex]?.mfaTotpSecret) {
        const otpConfig = providers.find(
          (c) => c.providerType === 'OTP' && c.enabled,
        );

        if (!otpConfig || otpConfig.totpSecret?.trim()) {
          const newErrors = [...errors];
          const currentErrors = { ...newErrors[credentialIndex] };

          delete currentErrors.mfaTotpSecret;
          newErrors[credentialIndex] = currentErrors;
          setErrors(newErrors);
        }
      }
    };

    const handleCredentialHeadersChange = (
      index: number,
      headers: Array<{ key: string; value: string }>,
    ) => {
      const newCredentials = [...credentials];

      newCredentials[index] = {
        ...newCredentials[index],
        headers,
      };
      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      if (errors[index] && Object.keys(errors[index]).length > 0) {
        const newErrors = [...errors];
        const currentErrors = { ...newErrors[index] };

        if (currentErrors.headers) {
          delete currentErrors.headers;
        }

        clearAuthenticationErrorIfValid(currentErrors, newCredentials[index]);
        newErrors[index] = currentErrors;
        setErrors(newErrors);
      }
    };

    const handleCredentialSecretsChange = (
      index: number,
      secrets: Array<{ key: string; value: string }>,
    ) => {
      const newCredentials = [...credentials];

      newCredentials[index] = {
        ...newCredentials[index],
        secrets,
      };
      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      if (errors[index] && Object.keys(errors[index]).length > 0) {
        const newErrors = [...errors];
        const currentErrors = { ...newErrors[index] };

        if (currentErrors.secrets) {
          delete currentErrors.secrets;
        }

        if (currentErrors.bodySignature) {
          delete currentErrors.bodySignature;
        }

        clearAuthenticationErrorIfValid(currentErrors, newCredentials[index]);
        newErrors[index] = currentErrors;
        setErrors(newErrors);
      }
    };

    const handleBodySignatureChange = ({
      index,
      field,
      value,
    }: {
      index: number;
      field: 'headerName' | 'algorithm' | 'secret';
      value: string;
    }) => {
      const newCredentials = [...credentials];
      const currentBodySignature = newCredentials[index].bodySignature ?? {
        headerName: '',
        algorithm: 'sha256',
        secret: '',
      };
      const nextBodySignature = {
        ...currentBodySignature,
        [field]:
          field === 'algorithm'
            ? (value as BodySignatureConfiguration['algorithm'])
            : value,
      };
      const hasConfigurableField =
        nextBodySignature.headerName.trim().length > 0 ||
        nextBodySignature.secret.trim().length > 0;

      newCredentials[index] = {
        ...newCredentials[index],
        bodySignature: hasConfigurableField ? nextBodySignature : null,
      };
      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      if (errors[index] && Object.keys(errors[index]).length > 0) {
        const newErrors = [...errors];
        const currentErrors = { ...newErrors[index] };

        if (currentErrors.bodySignature) {
          delete currentErrors.bodySignature;
        }

        clearAuthenticationErrorIfValid(currentErrors, newCredentials[index]);
        newErrors[index] = currentErrors;
        setErrors(newErrors);
      }
    };

    const handleBasicAuthChange = ({
      index,
      field,
      value,
    }: {
      index: number;
      field: 'username' | 'password';
      value: string;
    }) => {
      const newCredentials = [...credentials];

      newCredentials[index] = {
        ...newCredentials[index],
        ...(field === 'username'
          ? { basicAuthUsername: value }
          : { basicAuthPassword: value }),
      };
      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      if (errors[index] && Object.keys(errors[index]).length > 0) {
        const newErrors = [...errors];
        const currentErrors = { ...newErrors[index] };

        if (field === 'username' && currentErrors.basicAuthUsername) {
          delete currentErrors.basicAuthUsername;
        }

        if (field === 'password' && currentErrors.basicAuthPassword) {
          delete currentErrors.basicAuthPassword;
        }

        const nextBasicAuth = getBasicAuthCredentials({
          credential: newCredentials[index],
        });

        if (
          nextBasicAuth.username.trim().length > 0 &&
          nextBasicAuth.password.trim().length > 0
        ) {
          delete currentErrors.basicAuthUsername;
          delete currentErrors.basicAuthPassword;
        }

        clearAuthenticationErrorIfValid(currentErrors, newCredentials[index]);
        newErrors[index] = currentErrors;
        setErrors(newErrors);
      }
    };

    const handleMutualTlsFieldChange = ({
      index,
      field,
      value,
    }: {
      index: number;
      field:
        | 'mtlsOrigin'
        | 'mtlsCert'
        | 'mtlsKey'
        | 'mtlsPfx'
        | 'mtlsPassphrase';
      value: string;
    }) => {
      const currentCredential = credentials[index];

      if (!currentCredential) {
        return;
      }

      handleMutualTlsCredentialChange({
        index,
        nextCredential: {
          ...currentCredential,
          [field]: value,
        },
        changedFields: [field],
      });
    };

    const handleMutualTlsCredentialChange = ({
      index,
      nextCredential,
      changedFields,
    }: {
      index: number;
      nextCredential: ApplicationCredential;
      changedFields: Array<
        'mtlsOrigin' | 'mtlsCert' | 'mtlsKey' | 'mtlsPfx' | 'mtlsPassphrase'
      >;
    }) => {
      const newCredentials = [...credentials];

      newCredentials[index] = nextCredential;
      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      if (errors[index] && Object.keys(errors[index]).length > 0) {
        const newErrors = [...errors];
        const currentErrors = { ...newErrors[index] };

        if (changedFields.includes('mtlsOrigin') && currentErrors.mtlsOrigin) {
          delete currentErrors.mtlsOrigin;
        }

        if (changedFields.includes('mtlsCert') && currentErrors.mtlsCert) {
          delete currentErrors.mtlsCert;
        }

        if (changedFields.includes('mtlsPfx') && currentErrors.mtlsCert) {
          delete currentErrors.mtlsCert;
        }

        if (changedFields.includes('mtlsKey') && currentErrors.mtlsKey) {
          delete currentErrors.mtlsKey;
        }

        const nextMutualTls = getMutualTlsConfig({
          credential: newCredentials[index],
        });

        if (
          (nextMutualTls.pfx.trim().length > 0 ||
            (nextMutualTls.cert.trim().length > 0 &&
              nextMutualTls.key.trim().length > 0)) &&
          nextMutualTls.origin.trim().length > 0
        ) {
          delete currentErrors.mtlsOrigin;
          delete currentErrors.mtlsCert;
          delete currentErrors.mtlsKey;
        }

        clearAuthenticationErrorIfValid(currentErrors, newCredentials[index]);
        newErrors[index] = currentErrors;
        setErrors(newErrors);
      }
    };

    const handleMutualTlsFileChange = async ({
      index,
      field,
      file,
    }: {
      index: number;
      field: 'mtlsCert' | 'mtlsKey';
      file: File | null;
    }) => {
      if (!file) {
        if (field === 'mtlsCert') {
          const currentCredential = credentials[index];

          if (!currentCredential) {
            return;
          }

          handleMutualTlsCredentialChange({
            index,
            nextCredential: {
              ...currentCredential,
              mtlsCert: '',
              mtlsPfx: '',
              mtlsPassphrase: '',
            },
            changedFields: ['mtlsCert', 'mtlsPfx', 'mtlsPassphrase'],
          });
        } else {
          handleMutualTlsFieldChange({
            index,
            field,
            value: '',
          });
        }

        return;
      }

      const valueType =
        field === 'mtlsCert' && isPkcs12File({ fileName: file.name })
          ? 'pfx'
          : 'pem';
      const content =
        valueType === 'pfx'
          ? arrayBufferToBase64({ buffer: await file.arrayBuffer() })
          : await file.text();
      const currentCredential = credentials[index];

      if (!currentCredential) {
        return;
      }

      const nextCredential = applyMutualTlsFileChange({
        credential: currentCredential,
        field,
        value: content,
        valueType,
        applicationUrl,
      });
      const changedFields: Array<
        'mtlsOrigin' | 'mtlsCert' | 'mtlsKey' | 'mtlsPfx' | 'mtlsPassphrase'
      > =
        valueType === 'pfx'
          ? ['mtlsCert', 'mtlsKey', 'mtlsPfx']
          : [field, 'mtlsPfx', 'mtlsPassphrase'];

      if (
        (currentCredential.mtlsOrigin?.trim() ?? '') === '' &&
        (nextCredential.mtlsOrigin?.trim() ?? '') !== ''
      ) {
        changedFields.push('mtlsOrigin');
      }

      handleMutualTlsCredentialChange({
        index,
        nextCredential,
        changedFields,
      });
    };

    const triggerMutualTlsFilePicker = ({
      index,
      field,
    }: {
      index: number;
      field: 'mtlsCert' | 'mtlsKey';
    }) => {
      const refKey = `${index}-${field}`;

      mtlsFileInputRefs.current[refKey]?.click();
    };

    const clearMutualTlsFile = ({
      index,
      field,
    }: {
      index: number;
      field: 'mtlsCert' | 'mtlsKey';
    }) => {
      const refKey = `${index}-${field}`;
      const inputElement = mtlsFileInputRefs.current[refKey];

      if (inputElement) {
        inputElement.value = '';
      }

      if (field === 'mtlsCert') {
        const currentCredential = credentials[index];

        if (!currentCredential) {
          return;
        }

        handleMutualTlsCredentialChange({
          index,
          nextCredential: {
            ...currentCredential,
            mtlsCert: '',
            mtlsPfx: '',
            mtlsPassphrase: '',
          },
          changedFields: ['mtlsCert', 'mtlsPfx', 'mtlsPassphrase'],
        });

        return;
      }

      handleMutualTlsFieldChange({
        index,
        field,
        value: '',
      });
    };

    const handleCredentialCookiesChange = (
      index: number,
      cookies: Array<{ name: string; value: string }>,
    ) => {
      const newCredentials = [...credentials];

      newCredentials[index] = {
        ...newCredentials[index],
        cookies,
      };
      setIsDirtyState(true);
      emitCredentialsChange(newCredentials);
      // Clear cookies-specific errors when cookies change
      if (errors[index] && Object.keys(errors[index]).length > 0) {
        const newErrors = [...errors];
        const currentErrors = { ...newErrors[index] };

        // Clear cookies errors
        if (currentErrors.cookies) {
          delete currentErrors.cookies;
        }

        clearAuthenticationErrorIfValid(currentErrors, newCredentials[index]);
        newErrors[index] = currentErrors;
        setErrors(newErrors);
      }
    };

    const handleAddCookie = (index: number) => {
      const credential = credentials[index];
      const newCookies = [...credential.cookies, { name: '', value: '' }];

      handleCredentialCookiesChange(index, newCookies);
    };

    const handleRemoveCookie = (index: number, cookieIndex: number) => {
      const credential = credentials[index];
      const newCookies = credential.cookies.filter((_, i) => i !== cookieIndex);

      handleCredentialCookiesChange(index, newCookies);
    };

    const handleCookieChange = (
      index: number,
      cookieIndex: number,
      field: 'name' | 'value',
      value: string,
    ) => {
      const credential = credentials[index];
      const newCookies = [...credential.cookies];

      newCookies[cookieIndex] = { ...newCookies[cookieIndex], [field]: value };
      handleCredentialCookiesChange(index, newCookies);
    };

    const handleAddHeader = (index: number) => {
      const credential = credentials[index];

      if (credential.headers.length < 5) {
        const newHeaders = [...credential.headers, { key: '', value: '' }];

        handleCredentialHeadersChange(index, newHeaders);
      }
    };

    const handleRemoveHeader = (index: number, headerIndex: number) => {
      const credential = credentials[index];
      const newHeaders = credential.headers.filter((_, i) => i !== headerIndex);

      handleCredentialHeadersChange(index, newHeaders);
    };

    const handleHeaderChange = (
      index: number,
      headerIndex: number,
      field: 'key' | 'value',
      value: string,
    ) => {
      const credential = credentials[index];
      const newHeaders = [...credential.headers];

      newHeaders[headerIndex] = { ...newHeaders[headerIndex], [field]: value };
      handleCredentialHeadersChange(index, newHeaders);
    };

    const handleAddSecret = (index: number) => {
      const credential = credentials[index];
      const newSecrets = [...credential.secrets, { key: '', value: '' }];

      handleCredentialSecretsChange(index, newSecrets);
    };

    const handleRemoveSecret = (index: number, secretIndex: number) => {
      const credential = credentials[index];
      const newSecrets = credential.secrets.filter((_, i) => i !== secretIndex);

      handleCredentialSecretsChange(index, newSecrets);
    };

    const handleSecretChange = (
      index: number,
      secretIndex: number,
      field: 'key' | 'value',
      value: string,
    ) => {
      const credential = credentials[index];
      const newSecrets = [...credential.secrets];

      newSecrets[secretIndex] = { ...newSecrets[secretIndex], [field]: value };
      handleCredentialSecretsChange(index, newSecrets);
    };

    return (
      <VStack w="full" alignItems="stretch" gap={2} overflow="hidden">
        {title && (
          <Text fontSize="lg" fontWeight="semibold" color="fg.default">
            {title}
          </Text>
        )}
        {description && (
          <Text fontSize="sm" color="fg.muted" mb={2}>
            {description}
          </Text>
        )}
        {(intent === 'create' || credentials.length === 0) && (
          <Box borderRadius="lg" bg="bg.muted" borderWidth="1px">
            <HStack alignItems="center" p={2}>
              <Switch
                checked={requiresCredentials}
                onCheckedChange={handleRequiresCredentialsToggle}
              >
                <Text fontSize="sm" fontWeight="medium">
                  {requiresCredentials
                    ? t('requiresCredentials')
                    : t('noCredentialsRequired')}
                </Text>
              </Switch>
            </HStack>
          </Box>
        )}
        {requiresCredentials && (
          <VStack alignItems="stretch" gap={4} overflow="hidden">
            <HStack justifyContent="flex-end" alignItems="center" w="full">
              <Button variant="outline" size="xs" onClick={handleAddCredential}>
                <Add01Icon size={14} />
                {t('addCredential')}
              </Button>
            </HStack>
            <Box
              ref={scrollRef}
              overflow="auto"
              display="grid"
              columnGap={2}
              gridTemplateColumns={CREDENTIAL_ROW_GRID_TEMPLATE_COLUMNS}
            >
              {credentials.map((credential, index) => {
                const basicAuth = getBasicAuthCredentials({ credential });
                const defaultMutualTlsOrigin = getOriginFromApplicationUrl({
                  applicationUrl,
                });
                const isVerificationInProgress =
                  isCredentialVerificationInProgress({
                    status: credential.verification?.status,
                  });
                const visibleSecrets = credential.secrets;
                const visibleSecretIndexMap = credential.secrets.map(
                  (_secret, secretIndex) => secretIndex,
                );

                return (
                  <Box
                    key={index}
                    gridColumn="1 / -1"
                    display="grid"
                    p={3}
                    borderRadius="lg"
                    borderWidth="1px"
                    bg="bg.default"
                    borderColor={
                      errors[index] && hasCredentialErrors(errors[index])
                        ? 'red.10'
                        : 'border.default'
                    }
                    opacity={credential.isDisabled ? 0.6 : 1}
                    overflow="hidden"
                    mb={4}
                    rowGap={collapsedCredentials[index] ? 0 : 4}
                    alignItems="center"
                    gridTemplateColumns="subgrid"
                  >
                    <HStack
                      alignItems="center"
                      gap={1.5}
                      minW={0}
                      overflow="hidden"
                    >
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => toggleCredentialCollapse(index)}
                        aria-label={
                          collapsedCredentials[index]
                            ? 'Expand credential'
                            : 'Collapse credential'
                        }
                        flexShrink={0}
                      >
                        {collapsedCredentials[index] ? (
                          <ArrowRight01Icon size={14} />
                        ) : (
                          <ArrowDown01Icon size={14} />
                        )}
                      </Button>
                      <HStack
                        gap={2.5}
                        alignItems="center"
                        flex={1}
                        minW={0}
                        overflow="hidden"
                      >
                        <HStack gap={1} alignItems="center" flexShrink={0}>
                          <UserIcon size={12} color="var(--colors-fg-muted)" />
                          <Text
                            fontSize="xs"
                            color="fg.muted"
                            whiteSpace="nowrap"
                          >
                            {t('username')}:
                          </Text>
                          <Text
                            fontSize="xs"
                            color="fg.default"
                            whiteSpace="nowrap"
                          >
                            {credential.username?.trim() || t('noUsername')}
                          </Text>
                        </HStack>
                        <Text fontSize="xs" color="fg.muted">
                          •
                        </Text>
                        <HStack gap={1} alignItems="center" flexShrink={0}>
                          <Key01Icon size={12} color="var(--colors-fg-muted)" />
                          <Text
                            fontSize="xs"
                            color="fg.muted"
                            whiteSpace="nowrap"
                          >
                            {t('role')}:
                          </Text>
                          <Text
                            fontSize="xs"
                            color="fg.default"
                            whiteSpace="nowrap"
                          >
                            {resolveCredentialRoleLabel({
                              credential,
                              customRoles: availableCustomRoles,
                            }) || t('unknownRole')}
                          </Text>
                        </HStack>
                      </HStack>
                    </HStack>
                    <HStack
                      gap={3}
                      alignItems="center"
                      justifyContent="flex-end"
                      alignSelf="center"
                      flexShrink={0}
                    >
                      <FeatureAnalysisCredentialBadge
                        generatedBy={credential.generatedBy}
                      />
                      {credential.verification?.status &&
                        (credential.verification.status === 'VERIFIED' ||
                          credential.verification.status === 'FAILED' ||
                          isVerificationInProgress) && (
                          <CredentialVerifiedBadge
                            status={credential.verification.status}
                            verifiedAt={credential.verification.verifiedAt}
                            showSessionTooltip={showSessionTooltip}
                            verificationArtifacts={
                              credential.verification.artifacts
                            }
                          />
                        )}
                      {!credential.id && (
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => handleRemoveUnsavedCredential(index)}
                        >
                          <Delete01Icon size={14} />
                          {t('removeConfirmation.confirm')}
                        </Button>
                      )}
                    </HStack>
                    {credentials.length >= 1 ? (
                      <HStack
                        gap={1}
                        alignItems="center"
                        justifyContent="flex-end"
                        w="full"
                      >
                        {onSSESubscribe &&
                          (testingById[credential.id || ''] ||
                            isVerificationInProgress) &&
                          streamingIdById[credential.id || ''] && (
                            <IconButton
                              aria-label={`View live browser stream for credential ${credential.id}`}
                              variant="subtle"
                              size="sm"
                              onClick={() =>
                                setOpenModalCredentialId(credential.id || null)
                              }
                            >
                              <Tv01Icon size={14} />
                            </IconButton>
                          )}
                        {(testingById[credential.id || ''] ||
                          isVerificationInProgress) &&
                        onAbortVerification ? (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() =>
                              handleAbortVerification(credential.id)
                            }
                          >
                            <SquareIcon size={14} />
                            {t('abort')}
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => handleTestCredential(credential.id)}
                            disabled={
                              !credential.id ||
                              !!testingById[credential.id] ||
                              isVerificationInProgress ||
                              credential.isDisabled
                            }
                          >
                            <PlayIcon size={14} />
                            {t('test')}
                          </Button>
                        )}
                        {credential.id && onToggleCredentialStatus && (
                          <Tooltip
                            title={
                              credential.isDisabled
                                ? t('toggleStatus.enableTooltip')
                                : t('toggleStatus.disableTooltip')
                            }
                          >
                            <Box>
                              <Switch
                                checked={!credential.isDisabled}
                                onCheckedChange={() =>
                                  handleToggleCredentialStatus(
                                    credential.id,
                                    credential.isDisabled || false,
                                  )
                                }
                                size="sm"
                              />
                            </Box>
                          </Tooltip>
                        )}
                      </HStack>
                    ) : (
                      <Box />
                    )}
                    {!collapsedCredentials[index] && (
                      <Box gridColumn="1 / -1" overflow="hidden">
                        <VStack alignItems="stretch" gap={4} overflow="hidden">
                          {credential.verification?.status === 'FAILED' &&
                            credential.verification?.lastError && (
                              <Text fontSize="sm" color="fg.error" m={1}>
                                {credential.verification?.lastError}
                              </Text>
                            )}
                          <Collapsible.Root
                            open={!isSectionCollapsed(index, 'basicInfo')}
                            onOpenChange={({ open }) =>
                              setCollapsedSections((prev) => ({
                                ...prev,
                                [index]: {
                                  ...prev[index],
                                  basicInfo: !open,
                                },
                              }))
                            }
                          >
                            <Collapsible.Trigger asChild>
                              <Box
                                as="button"
                                w="full"
                                textAlign="left"
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                                py={2}
                                px={2}
                                cursor="pointer"
                                bg="transparent"
                                borderRadius="md"
                                border="none"
                                _hover={{ bg: 'bg.muted' }}
                              >
                                <VStack
                                  alignItems="flex-start"
                                  gap={0.5}
                                  flex={1}
                                >
                                  <Text
                                    fontSize="md"
                                    fontWeight="medium"
                                    color="fg.default"
                                  >
                                    {t('sections.basicInfo.title')}
                                  </Text>
                                  <Text fontSize="xs" color="fg.muted">
                                    {t('sections.basicInfo.description')}
                                  </Text>
                                </VStack>
                                {isSectionCollapsed(index, 'basicInfo') ? (
                                  <ArrowRight01Icon size={14} />
                                ) : (
                                  <ArrowDown01Icon size={14} />
                                )}
                              </Box>
                            </Collapsible.Trigger>
                            <Collapsible.Content>
                              <Box pl={4} pt={2}>
                                <VStack alignItems="stretch" gap={3}>
                                  <HStack
                                    gap={4}
                                    w="full"
                                    overflow="hidden"
                                    alignItems="baseline"
                                  >
                                    <VStack
                                      alignItems="stretch"
                                      flex={1}
                                      minW={0}
                                    >
                                      <Text fontSize="sm" fontWeight="medium">
                                        {t('username')}{' '}
                                        <Text as="span" color="fg.error">
                                          *
                                        </Text>
                                      </Text>
                                      <HStack gap={2} alignItems="center">
                                        <Input
                                          placeholder={t('usernamePlaceholder')}
                                          value={credential.username}
                                          flex={1}
                                          onChange={(e) =>
                                            handleCredentialStringChange(
                                              index,
                                              'username',
                                              e.target.value,
                                            )
                                          }
                                        />
                                        {onGenerateMailbox &&
                                          (() => {
                                            const isGenerating =
                                              mailboxGeneratingIndex === index;
                                            const isSuccess =
                                              mailboxSuccessIndex === index;
                                            const hasMailbox =
                                              credentialHasResolvableMailbox({
                                                credential,
                                              });
                                            const tooltipTitle = isSuccess
                                              ? t('mailboxGenerated')
                                              : hasMailbox
                                                ? t('mailboxAlreadyGenerated')
                                                : t('generateMailbox');

                                            return (
                                              <Tooltip
                                                title={tooltipTitle}
                                                subtitle={
                                                  isSuccess || hasMailbox
                                                    ? undefined
                                                    : t(
                                                        'generateMailboxDescription',
                                                      )
                                                }
                                                positioning={{
                                                  placement: 'top',
                                                }}
                                              >
                                                <Box as="span">
                                                  <IconButton
                                                    aria-label={t(
                                                      'generateMailbox',
                                                    )}
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={
                                                      isGenerating || hasMailbox
                                                    }
                                                    onClick={async () => {
                                                      setMailboxGeneratingIndex(
                                                        index,
                                                      );
                                                      setMailboxSuccessIndex(
                                                        null,
                                                      );

                                                      try {
                                                        const response =
                                                          await onGenerateMailbox();

                                                        if (!response.success)
                                                          return;
                                                        const nextCredentials =
                                                          [...credentials];

                                                        nextCredentials[index] =
                                                          {
                                                            ...nextCredentials[
                                                              index
                                                            ],
                                                            username:
                                                              response.emailAddress ||
                                                              credential.username,
                                                            generatedMailboxAddress:
                                                              response.emailAddress ??
                                                              null,
                                                          };
                                                        setIsDirtyState(true);
                                                        emitCredentialsChange(
                                                          nextCredentials,
                                                        );
                                                        if (
                                                          errors[index]
                                                            ?.username
                                                        ) {
                                                          const nextErrors = [
                                                            ...errors,
                                                          ];
                                                          const currentErrors =
                                                            {
                                                              ...nextErrors[
                                                                index
                                                              ],
                                                            };

                                                          delete currentErrors.username;
                                                          nextErrors[index] =
                                                            currentErrors;
                                                          setErrors(nextErrors);
                                                        }

                                                        setMailboxSuccessIndex(
                                                          index,
                                                        );
                                                        setTimeout(
                                                          () =>
                                                            setMailboxSuccessIndex(
                                                              (prev) =>
                                                                prev === index
                                                                  ? null
                                                                  : prev,
                                                            ),
                                                          2000,
                                                        );
                                                      } finally {
                                                        setMailboxGeneratingIndex(
                                                          (prev) =>
                                                            prev === index
                                                              ? null
                                                              : prev,
                                                        );
                                                      }
                                                    }}
                                                  >
                                                    {isGenerating ? (
                                                      <Box
                                                        animation="spin 1s linear infinite"
                                                        display="flex"
                                                      >
                                                        <Loading03Icon
                                                          size={16}
                                                        />
                                                      </Box>
                                                    ) : isSuccess ? (
                                                      <Tick01Icon size={16} />
                                                    ) : (
                                                      <InboxIcon size={16} />
                                                    )}
                                                  </IconButton>
                                                </Box>
                                              </Tooltip>
                                            );
                                          })()}
                                      </HStack>
                                      {errors[index]?.username && (
                                        <Text fontSize="sm" color="fg.error">
                                          {errors[index].username}
                                        </Text>
                                      )}
                                    </VStack>
                                    <VStack
                                      alignItems="stretch"
                                      flex={1}
                                      minW={0}
                                    >
                                      <Text fontSize="sm" fontWeight="medium">
                                        {t('password')}
                                      </Text>
                                      <SensitiveInput
                                        value={credential.password || ''}
                                        placeholder={t('passwordPlaceholder')}
                                        onChange={(value) =>
                                          handleCredentialStringChange(
                                            index,
                                            'password',
                                            value,
                                          )
                                        }
                                        fieldKey={`${index}-password`}
                                        allowViewingCredentials={
                                          allowViewingCredentials
                                        }
                                        onCopy={onCopy}
                                        copiedState={
                                          copiedCredentials[
                                            `${index}-password`
                                          ] || false
                                        }
                                        visibleState={
                                          visibleCredentials[
                                            `${index}-password`
                                          ] || false
                                        }
                                        onToggleVisibility={
                                          toggleCredentialVisibility
                                        }
                                        onCopySuccess={() =>
                                          handleCopySuccess(`${index}-password`)
                                        }
                                        type="password"
                                        isDefaultValue={isDefaultSecretValue(
                                          credential.password,
                                        )}
                                      />
                                      {errors[index]?.authentication && (
                                        <Text fontSize="sm" color="fg.error">
                                          {errors[index].authentication}
                                        </Text>
                                      )}
                                    </VStack>
                                  </HStack>
                                  {credentialHasResolvableMailbox({
                                    credential,
                                  }) && (
                                    <GeneratedMailboxSection
                                      emailAddress={resolveGeneratedMailboxAddress(
                                        { credential },
                                      )}
                                    >
                                      {onFetchCredentialInbox &&
                                        credential.id && (
                                          <CredentialInboxMessagesPanel
                                            credentialId={credential.id}
                                            onFetchCredentialInbox={
                                              onFetchCredentialInbox
                                            }
                                          />
                                        )}
                                    </GeneratedMailboxSection>
                                  )}
                                  <VStack alignItems="stretch">
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('role')}{' '}
                                      <Text as="span" color="fg.error">
                                        *
                                      </Text>
                                    </Text>
                                    {availableCustomRoles.length > 0 ||
                                    onCreateRole ? (
                                      <RoleSelectionDropdown
                                        value={
                                          credential.roleId
                                            ? `custom:${credential.roleId}`
                                            : customRoleInputs[index]
                                              ? 'OTHER'
                                              : credential.role
                                        }
                                        customRoles={availableCustomRoles}
                                        fallbackRoleName={
                                          credential.roleId &&
                                          !availableCustomRoles.find(
                                            (r) => r.id === credential.roleId,
                                          )
                                            ? credential.role
                                            : undefined
                                        }
                                        onValueChange={(value, roleId) =>
                                          handleRoleChange(index, value, roleId)
                                        }
                                        onCreateRole={onCreateRole}
                                      />
                                    ) : (
                                      <Select.Root
                                        positioning={{ sameWidth: true }}
                                        value={[
                                          customRoleInputs[index]
                                            ? 'OTHER'
                                            : credential.role,
                                        ]}
                                        collection={roleOptions}
                                        onValueChange={(e) =>
                                          handleRoleChange(index, e.value[0])
                                        }
                                      >
                                        <Select.Control>
                                          <Select.Trigger>
                                            <Select.ValueText />
                                            <ArrowUpDownIcon size={16} />
                                          </Select.Trigger>
                                        </Select.Control>
                                        <Select.Positioner>
                                          <Select.Content>
                                            <Select.ItemGroup>
                                              {roleOptions.items.map(
                                                (option) => (
                                                  <Select.Item
                                                    key={option.value}
                                                    item={option}
                                                  >
                                                    <Select.ItemText>
                                                      {option.label}
                                                    </Select.ItemText>
                                                    <Select.ItemIndicator>
                                                      <Tick01Icon size={16} />
                                                    </Select.ItemIndicator>
                                                  </Select.Item>
                                                ),
                                              )}
                                            </Select.ItemGroup>
                                          </Select.Content>
                                        </Select.Positioner>
                                      </Select.Root>
                                    )}
                                    {customRoleInputs[index] && (
                                      <Input
                                        placeholder={t('customRolePlaceholder')}
                                        value={credential.role}
                                        onChange={(e) =>
                                          handleCredentialStringChange(
                                            index,
                                            'role',
                                            e.target.value,
                                          )
                                        }
                                      />
                                    )}
                                    {errors[index]?.role && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].role}
                                      </Text>
                                    )}
                                  </VStack>
                                  <VStack alignItems="stretch" gap={1}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('roleDescription')}
                                    </Text>
                                    <Field.Root>
                                      <Field.Textarea
                                        placeholder={t(
                                          'roleDescriptionPlaceholder',
                                        )}
                                        value={
                                          credential.roleId
                                            ? credential.applicationRole
                                                ?.description || ''
                                            : credential.roleDescription || ''
                                        }
                                        onChange={(e) => {
                                          if (!credential.roleId) {
                                            handleCredentialStringChange(
                                              index,
                                              'roleDescription',
                                              e.target.value,
                                            );
                                          }
                                        }}
                                        disabled={!!credential.roleId}
                                        rows={3}
                                      />
                                      <Field.HelperText>
                                        <Text fontSize="xs" color="fg.muted">
                                          {credential.roleId
                                            ? t('roleDescriptionHelperCustom')
                                            : t('roleDescriptionHelper')}
                                        </Text>
                                      </Field.HelperText>
                                    </Field.Root>
                                  </VStack>
                                  <VStack alignItems="stretch" gap={1}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('loginUrl')}
                                    </Text>
                                    <Field.Root
                                      invalid={!!errors[index]?.loginUrl}
                                    >
                                      <Field.Input
                                        type="url"
                                        placeholder={t('loginUrlPlaceholder')}
                                        value={credential.loginUrl || ''}
                                        onChange={(e) =>
                                          handleCredentialStringChange(
                                            index,
                                            'loginUrl',
                                            e.target.value,
                                          )
                                        }
                                      />
                                      {errors[index]?.loginUrl && (
                                        <Field.ErrorText>
                                          {errors[index].loginUrl}
                                        </Field.ErrorText>
                                      )}
                                      {!errors[index]?.loginUrl && (
                                        <Field.HelperText>
                                          <Text fontSize="xs" color="fg.muted">
                                            {t('loginUrlHelper')}
                                          </Text>
                                        </Field.HelperText>
                                      )}
                                    </Field.Root>
                                  </VStack>
                                  <VStack alignItems="stretch">
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('ssoProvider')}
                                    </Text>
                                    <Select.Root
                                      positioning={{ sameWidth: true }}
                                      value={[
                                        customSsoProviders[index]
                                          ? 'OTHER'
                                          : credential.ssoProvider,
                                      ]}
                                      collection={ssoProviderOptions}
                                      onValueChange={(e) =>
                                        handleSsoProviderChange(
                                          index,
                                          e.value[0],
                                        )
                                      }
                                    >
                                      <Select.Control>
                                        <Select.Trigger>
                                          <Select.ValueText />
                                          <ArrowUpDownIcon size={16} />
                                        </Select.Trigger>
                                      </Select.Control>
                                      <Select.Positioner>
                                        <Select.Content>
                                          <Select.ItemGroup>
                                            {ssoProviderOptions.items.map(
                                              (option) => (
                                                <Select.Item
                                                  key={option.value}
                                                  item={option}
                                                >
                                                  <Select.ItemText>
                                                    {option.label}
                                                  </Select.ItemText>
                                                  <Select.ItemIndicator>
                                                    <Tick01Icon size={16} />
                                                  </Select.ItemIndicator>
                                                </Select.Item>
                                              ),
                                            )}
                                          </Select.ItemGroup>
                                        </Select.Content>
                                      </Select.Positioner>
                                    </Select.Root>
                                    {customSsoProviders[index] && (
                                      <Input
                                        placeholder={t(
                                          'customSsoProviderPlaceholder',
                                        )}
                                        value={credential.ssoProvider}
                                        onChange={(e) =>
                                          handleCredentialStringChange(
                                            index,
                                            'ssoProvider',
                                            e.target.value,
                                          )
                                        }
                                      />
                                    )}
                                  </VStack>
                                  <VStack alignItems="stretch">
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('tenant')}
                                    </Text>
                                    <Input
                                      placeholder={t('tenantPlaceholder')}
                                      value={credential.tenant || ''}
                                      onChange={(e) =>
                                        handleCredentialStringChange(
                                          index,
                                          'tenant',
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </VStack>
                                  <VStack alignItems="stretch" gap={2}>
                                    <MfaConfigComponent
                                      mfaProviders={
                                        credential.mfaProviders ?? []
                                      }
                                      onMfaProvidersChange={(providers) =>
                                        handleMfaProvidersChange(
                                          index,
                                          providers,
                                        )
                                      }
                                      credentialId={credential.id}
                                      applicationId={applicationId}
                                      onFetchMfaMessages={onFetchMfaMessages}
                                      onFetchMfaWebhookConfig={
                                        onFetchMfaWebhookConfig
                                      }
                                      totpSecretReveal={{
                                        totpSecretFieldKey: `${index}-totpSecret`,
                                        allowViewingCredentials: Boolean(
                                          allowViewingCredentials,
                                        ),
                                        onCopy,
                                        copiedState:
                                          copiedCredentials[
                                            `${index}-totpSecret`
                                          ] ?? false,
                                        visibleState:
                                          visibleCredentials[
                                            `${index}-totpSecret`
                                          ] ?? false,
                                        onToggleVisibility:
                                          toggleCredentialVisibility,
                                        onCopySuccess: () =>
                                          handleCopySuccess(
                                            `${index}-totpSecret`,
                                          ),
                                        isDefaultSecretValue:
                                          isDefaultSecretValue(
                                            credential.mfaProviders?.find(
                                              (row) =>
                                                row.providerType ===
                                                MfaProviderSchema.enum.OTP,
                                            )?.totpSecret,
                                          ),
                                      }}
                                    />
                                    {errors[index]?.mfaTotpSecret && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].mfaTotpSecret}
                                      </Text>
                                    )}
                                  </VStack>
                                </VStack>
                              </Box>
                            </Collapsible.Content>
                          </Collapsible.Root>
                          <Divider />
                          <Collapsible.Root
                            open={
                              !isSectionCollapsed(index, 'credentialDetails')
                            }
                            onOpenChange={({ open }) =>
                              setCollapsedSections((prev) => ({
                                ...prev,
                                [index]: {
                                  ...prev[index],
                                  credentialDetails: !open,
                                },
                              }))
                            }
                          >
                            <Collapsible.Trigger asChild>
                              <Box
                                as="button"
                                w="full"
                                textAlign="left"
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                gap={2}
                                py={2}
                                px={2}
                                cursor="pointer"
                                bg="transparent"
                                borderRadius="md"
                                border="none"
                                _hover={{ bg: 'bg.muted' }}
                              >
                                <VStack
                                  alignItems="flex-start"
                                  gap={0.5}
                                  flex={1}
                                >
                                  <Text
                                    fontSize="md"
                                    fontWeight="medium"
                                    color="fg.default"
                                  >
                                    {t('sections.credentialDetails.title')}
                                  </Text>
                                  <Text fontSize="xs" color="fg.muted">
                                    {t(
                                      'sections.credentialDetails.description',
                                    )}
                                  </Text>
                                </VStack>
                                {isSectionCollapsed(
                                  index,
                                  'credentialDetails',
                                ) ? (
                                  <ArrowRight01Icon size={14} />
                                ) : (
                                  <ArrowDown01Icon size={14} />
                                )}
                              </Box>
                            </Collapsible.Trigger>
                            <Collapsible.Content>
                              <Box pl={4} pt={2}>
                                <VStack alignItems="stretch" gap={3}>
                                  <VStack alignItems="stretch" gap={2}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('basicAuthTitle')}
                                    </Text>
                                    <HStack
                                      gap={2}
                                      w="full"
                                      overflow="hidden"
                                      alignItems="baseline"
                                    >
                                      <Input
                                        placeholder={t(
                                          'basicAuthUsernamePlaceholder',
                                        )}
                                        value={basicAuth.username}
                                        onChange={(e) =>
                                          handleBasicAuthChange({
                                            index,
                                            field: 'username',
                                            value: e.target.value,
                                          })
                                        }
                                        flex={1}
                                        minW={0}
                                      />
                                      <SensitiveInput
                                        value={basicAuth.password}
                                        placeholder={t(
                                          'basicAuthPasswordPlaceholder',
                                        )}
                                        onChange={(value) =>
                                          handleBasicAuthChange({
                                            index,
                                            field: 'password',
                                            value,
                                          })
                                        }
                                        fieldKey={`${index}-basic-auth-password`}
                                        allowViewingCredentials={
                                          allowViewingCredentials
                                        }
                                        onCopy={onCopy}
                                        copiedState={
                                          copiedCredentials[
                                            `${index}-basic-auth-password`
                                          ] || false
                                        }
                                        visibleState={
                                          visibleCredentials[
                                            `${index}-basic-auth-password`
                                          ] || false
                                        }
                                        onToggleVisibility={
                                          toggleCredentialVisibility
                                        }
                                        onCopySuccess={() =>
                                          handleCopySuccess(
                                            `${index}-basic-auth-password`,
                                          )
                                        }
                                        type="password"
                                        isDefaultValue={isDefaultSecretValue(
                                          basicAuth.password,
                                        )}
                                      />
                                    </HStack>
                                    {errors[index]?.basicAuthUsername && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].basicAuthUsername}
                                      </Text>
                                    )}
                                    {errors[index]?.basicAuthPassword && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].basicAuthPassword}
                                      </Text>
                                    )}
                                  </VStack>
                                  <Divider />
                                  <VStack alignItems="stretch" gap={2}>
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('mtlsTitle')}
                                    </Text>
                                    <Input
                                      placeholder={
                                        defaultMutualTlsOrigin ||
                                        t('mtlsOriginPlaceholder')
                                      }
                                      value={credential.mtlsOrigin ?? ''}
                                      onChange={(event) =>
                                        handleMutualTlsFieldChange({
                                          index,
                                          field: 'mtlsOrigin',
                                          value: event.target.value,
                                        })
                                      }
                                    />
                                    <SensitiveInput
                                      value={credential.mtlsPassphrase ?? ''}
                                      placeholder={t(
                                        'mtlsPassphrasePlaceholder',
                                      )}
                                      onChange={(value) =>
                                        handleMutualTlsFieldChange({
                                          index,
                                          field: 'mtlsPassphrase',
                                          value,
                                        })
                                      }
                                      fieldKey={`${index}-mtls-passphrase`}
                                      allowViewingCredentials={
                                        allowViewingCredentials
                                      }
                                      onCopy={onCopy}
                                      copiedState={
                                        copiedCredentials[
                                          `${index}-mtls-passphrase`
                                        ] || false
                                      }
                                      visibleState={
                                        visibleCredentials[
                                          `${index}-mtls-passphrase`
                                        ] || false
                                      }
                                      onToggleVisibility={
                                        toggleCredentialVisibility
                                      }
                                      onCopySuccess={() =>
                                        handleCopySuccess(
                                          `${index}-mtls-passphrase`,
                                        )
                                      }
                                      type="password"
                                      isDefaultValue={isDefaultSecretValue(
                                        credential.mtlsPassphrase ?? undefined,
                                      )}
                                      disabled={
                                        !hasStoredSecretValue(
                                          credential.mtlsPfx,
                                        )
                                      }
                                    />
                                    <HStack gap={3} flexWrap="wrap">
                                      <HStack gap={2}>
                                        {(() => {
                                          const hasStoredMtlsBundle =
                                            hasStoredSecretValue(
                                              credential.mtlsPfx,
                                            );
                                          const hasStoredMtlsCertLikeValue =
                                            hasStoredSecretValue(
                                              credential.mtlsCert,
                                            ) || hasStoredMtlsBundle;

                                          return (
                                            <>
                                              <Input
                                                type="file"
                                                display="none"
                                                accept=".pem,.crt,.cer,.p12,.pfx,.txt"
                                                ref={(element) => {
                                                  mtlsFileInputRefs.current[
                                                    `${index}-mtlsCert`
                                                  ] = element;
                                                }}
                                                onChange={(event) => {
                                                  void handleMutualTlsFileChange(
                                                    {
                                                      index,
                                                      field: 'mtlsCert',
                                                      file:
                                                        event.target
                                                          .files?.[0] ?? null,
                                                    },
                                                  );
                                                  event.target.value = '';
                                                }}
                                              />
                                              <Button
                                                size="sm"
                                                variant={
                                                  hasStoredMtlsCertLikeValue
                                                    ? 'subtle'
                                                    : 'outline'
                                                }
                                                onClick={() =>
                                                  triggerMutualTlsFilePicker({
                                                    index,
                                                    field: 'mtlsCert',
                                                  })
                                                }
                                                disabled={
                                                  hasStoredMtlsCertLikeValue
                                                }
                                                aria-label={t('mtlsCertUpload')}
                                              >
                                                {hasStoredMtlsCertLikeValue
                                                  ? t('mtlsCertUploaded')
                                                  : t('mtlsCertUpload')}
                                              </Button>
                                              {hasStoredMtlsCertLikeValue && (
                                                <IconButton
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() =>
                                                    clearMutualTlsFile({
                                                      index,
                                                      field: 'mtlsCert',
                                                    })
                                                  }
                                                  aria-label={t(
                                                    'mtlsDeleteCert',
                                                  )}
                                                >
                                                  <Delete01Icon size={14} />
                                                </IconButton>
                                              )}
                                            </>
                                          );
                                        })()}
                                      </HStack>
                                      <HStack gap={2}>
                                        <Input
                                          type="file"
                                          display="none"
                                          accept=".pem,.key,.txt"
                                          ref={(element) => {
                                            mtlsFileInputRefs.current[
                                              `${index}-mtlsKey`
                                            ] = element;
                                          }}
                                          onChange={(event) => {
                                            void handleMutualTlsFileChange({
                                              index,
                                              field: 'mtlsKey',
                                              file:
                                                event.target.files?.[0] ?? null,
                                            });
                                            event.target.value = '';
                                          }}
                                        />
                                        <Button
                                          size="sm"
                                          variant={
                                            hasStoredSecretValue(
                                              credential.mtlsKey,
                                            )
                                              ? 'subtle'
                                              : 'outline'
                                          }
                                          onClick={() =>
                                            triggerMutualTlsFilePicker({
                                              index,
                                              field: 'mtlsKey',
                                            })
                                          }
                                          disabled={
                                            hasStoredSecretValue(
                                              credential.mtlsKey,
                                            ) ||
                                            hasStoredSecretValue(
                                              credential.mtlsPfx,
                                            )
                                          }
                                          aria-label={t('mtlsKeyUpload')}
                                        >
                                          {hasStoredSecretValue(
                                            credential.mtlsKey,
                                          )
                                            ? t('mtlsKeyUploaded')
                                            : t('mtlsKeyUpload')}
                                        </Button>
                                        {hasStoredSecretValue(
                                          credential.mtlsKey,
                                        ) && (
                                          <IconButton
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              clearMutualTlsFile({
                                                index,
                                                field: 'mtlsKey',
                                              })
                                            }
                                            aria-label={t('mtlsDeleteKey')}
                                          >
                                            <Delete01Icon size={14} />
                                          </IconButton>
                                        )}
                                      </HStack>
                                    </HStack>
                                    {errors[index]?.mtlsCert && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].mtlsCert}
                                      </Text>
                                    )}
                                    {errors[index]?.mtlsKey && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].mtlsKey}
                                      </Text>
                                    )}
                                    {errors[index]?.mtlsOrigin && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].mtlsOrigin}
                                      </Text>
                                    )}
                                  </VStack>
                                  <Divider />
                                  <HStack
                                    justifyContent="space-between"
                                    alignItems="center"
                                  >
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('headers')}
                                    </Text>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleAddHeader(index)}
                                      disabled={credential.headers.length >= 5}
                                      aria-label={t('addHeader')}
                                    >
                                      <Add01Icon size={14} />
                                      {t('addHeader')}
                                    </Button>
                                  </HStack>
                                  {credential.headers &&
                                    credential.headers.length > 0 && (
                                      <VStack gap={2} alignItems="stretch">
                                        {credential.headers.map(
                                          (header, headerIndex) => (
                                            <VStack
                                              key={headerIndex}
                                              alignItems="stretch"
                                              gap={1}
                                            >
                                              <HStack
                                                gap={2}
                                                w="full"
                                                overflow="hidden"
                                                alignItems="baseline"
                                              >
                                                <Input
                                                  placeholder={t(
                                                    'headerNamePlaceholder',
                                                  )}
                                                  value={header.key}
                                                  onChange={(e) =>
                                                    handleHeaderChange(
                                                      index,
                                                      headerIndex,
                                                      'key',
                                                      e.target.value,
                                                    )
                                                  }
                                                  flex={1}
                                                  minW={0}
                                                />
                                                <SensitiveInput
                                                  value={header.value}
                                                  placeholder={t(
                                                    'headerValuePlaceholder',
                                                  )}
                                                  onChange={(value) =>
                                                    handleHeaderChange(
                                                      index,
                                                      headerIndex,
                                                      'value',
                                                      value,
                                                    )
                                                  }
                                                  fieldKey={`${index}-header-${headerIndex}-value`}
                                                  allowViewingCredentials={
                                                    allowViewingCredentials
                                                  }
                                                  onCopy={onCopy}
                                                  copiedState={
                                                    copiedCredentials[
                                                      `${index}-header-${headerIndex}-value`
                                                    ] || false
                                                  }
                                                  visibleState={
                                                    visibleCredentials[
                                                      `${index}-header-${headerIndex}-value`
                                                    ] || false
                                                  }
                                                  onToggleVisibility={
                                                    toggleCredentialVisibility
                                                  }
                                                  onCopySuccess={() =>
                                                    handleCopySuccess(
                                                      `${index}-header-${headerIndex}-value`,
                                                    )
                                                  }
                                                  type="text"
                                                  isDefaultValue={isDefaultSecretValue(
                                                    header.value,
                                                  )}
                                                />
                                                <IconButton
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() =>
                                                    handleRemoveHeader(
                                                      index,
                                                      headerIndex,
                                                    )
                                                  }
                                                  aria-label={t('removeHeader')}
                                                >
                                                  <Delete01Icon size={14} />
                                                </IconButton>
                                              </HStack>
                                              {errors[index]?.headers?.[
                                                headerIndex
                                              ] && (
                                                <Text
                                                  fontSize="sm"
                                                  color="fg.error"
                                                  ml={2}
                                                >
                                                  {
                                                    errors[index].headers?.[
                                                      headerIndex
                                                    ]
                                                  }
                                                </Text>
                                              )}
                                            </VStack>
                                          ),
                                        )}
                                      </VStack>
                                    )}
                                  <VStack alignItems="stretch" gap={3}>
                                    <HStack
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <Text fontSize="sm" fontWeight="medium">
                                        {t('cookies')}
                                      </Text>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleAddCookie(index)}
                                        aria-label={t('addCookie')}
                                      >
                                        <Add01Icon size={14} />
                                        {t('addCookie')}
                                      </Button>
                                    </HStack>
                                    {(credential.cookies || []).length > 0 && (
                                      <VStack gap={2} alignItems="stretch">
                                        {(credential.cookies || []).map(
                                          (cookie, cookieIndex) => (
                                            <VStack
                                              key={cookieIndex}
                                              alignItems="stretch"
                                              gap={1}
                                            >
                                              <HStack
                                                gap={2}
                                                w="full"
                                                overflow="hidden"
                                                alignItems="baseline"
                                              >
                                                <Input
                                                  placeholder={t(
                                                    'cookieNamePlaceholder',
                                                  )}
                                                  value={cookie.name}
                                                  onChange={(e) =>
                                                    handleCookieChange(
                                                      index,
                                                      cookieIndex,
                                                      'name',
                                                      e.target.value,
                                                    )
                                                  }
                                                  flex={1}
                                                  minW={0}
                                                />
                                                <SensitiveInput
                                                  value={cookie.value}
                                                  placeholder={t(
                                                    'cookieValuePlaceholder',
                                                  )}
                                                  onChange={(value) =>
                                                    handleCookieChange(
                                                      index,
                                                      cookieIndex,
                                                      'value',
                                                      value,
                                                    )
                                                  }
                                                  fieldKey={`${index}-cookie-${cookieIndex}-value`}
                                                  allowViewingCredentials={
                                                    allowViewingCredentials
                                                  }
                                                  onCopy={onCopy}
                                                  copiedState={
                                                    copiedCredentials[
                                                      `${index}-cookie-${cookieIndex}-value`
                                                    ] || false
                                                  }
                                                  visibleState={
                                                    visibleCredentials[
                                                      `${index}-cookie-${cookieIndex}-value`
                                                    ] || false
                                                  }
                                                  onToggleVisibility={
                                                    toggleCredentialVisibility
                                                  }
                                                  onCopySuccess={() =>
                                                    handleCopySuccess(
                                                      `${index}-cookie-${cookieIndex}-value`,
                                                    )
                                                  }
                                                  type="text"
                                                  isDefaultValue={isDefaultSecretValue(
                                                    cookie.value,
                                                  )}
                                                />
                                                <IconButton
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() =>
                                                    handleRemoveCookie(
                                                      index,
                                                      cookieIndex,
                                                    )
                                                  }
                                                  aria-label={t('removeCookie')}
                                                >
                                                  <Delete01Icon size={14} />
                                                </IconButton>
                                              </HStack>
                                              {errors[index]?.cookies?.[
                                                cookieIndex
                                              ] && (
                                                <Text
                                                  fontSize="sm"
                                                  color="fg.error"
                                                  ml={2}
                                                >
                                                  {
                                                    errors[index].cookies?.[
                                                      cookieIndex
                                                    ]
                                                  }
                                                </Text>
                                              )}
                                            </VStack>
                                          ),
                                        )}
                                      </VStack>
                                    )}
                                  </VStack>
                                  <VStack gap={2} alignItems="stretch">
                                    <HStack
                                      justifyContent="space-between"
                                      alignItems="center"
                                    >
                                      <HStack gap={2} alignItems="center">
                                        <Text fontSize="sm" fontWeight="medium">
                                          {t('secretKey')}
                                        </Text>
                                        <Tooltip
                                          openDelay={50}
                                          positioning={{
                                            placement: 'top',
                                            strategy: 'fixed',
                                          }}
                                          title={t('secretTooltip')}
                                          maxW="280px"
                                        >
                                          <IconButton
                                            variant="ghost"
                                            size="xs"
                                            aria-label="Additional Secrets Information"
                                          >
                                            <InformationCircleIcon size={14} />
                                          </IconButton>
                                        </Tooltip>
                                      </HStack>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleAddSecret(index)}
                                        aria-label={t('addSecret')}
                                      >
                                        <Add01Icon size={14} />
                                        {t('addSecret')}
                                      </Button>
                                    </HStack>
                                    {visibleSecrets.length > 0 && (
                                      <VStack gap={2} alignItems="stretch">
                                        {visibleSecrets.map(
                                          (secret, secretIndex) => (
                                            <VStack
                                              key={secretIndex}
                                              alignItems="stretch"
                                              gap={1}
                                            >
                                              <HStack
                                                gap={2}
                                                w="full"
                                                overflow="hidden"
                                                alignItems="baseline"
                                              >
                                                <Input
                                                  placeholder={t(
                                                    'secretKeyPlaceholder',
                                                  )}
                                                  value={secret.key}
                                                  onChange={(e) =>
                                                    handleSecretChange(
                                                      index,
                                                      visibleSecretIndexMap[
                                                        secretIndex
                                                      ] as number,
                                                      'key',
                                                      e.target.value,
                                                    )
                                                  }
                                                  flex={1}
                                                  minW={0}
                                                />
                                                <SensitiveInput
                                                  value={secret.value}
                                                  placeholder={t(
                                                    'secretValuePlaceholder',
                                                  )}
                                                  onChange={(value) =>
                                                    handleSecretChange(
                                                      index,
                                                      visibleSecretIndexMap[
                                                        secretIndex
                                                      ] as number,
                                                      'value',
                                                      value,
                                                    )
                                                  }
                                                  fieldKey={`${index}-secret-${secretIndex}`}
                                                  allowViewingCredentials={
                                                    allowViewingCredentials
                                                  }
                                                  onCopy={onCopy}
                                                  copiedState={
                                                    copiedCredentials[
                                                      `${index}-secret-${secretIndex}`
                                                    ] || false
                                                  }
                                                  visibleState={
                                                    visibleCredentials[
                                                      `${index}-secret-${secretIndex}`
                                                    ] || false
                                                  }
                                                  onToggleVisibility={
                                                    toggleCredentialVisibility
                                                  }
                                                  onCopySuccess={() =>
                                                    handleCopySuccess(
                                                      `${index}-secret-${secretIndex}`,
                                                    )
                                                  }
                                                  type="password"
                                                  isDefaultValue={isDefaultSecretValue(
                                                    secret.value,
                                                  )}
                                                />
                                                <IconButton
                                                  variant="ghost"
                                                  size="sm"
                                                  onClick={() =>
                                                    handleRemoveSecret(
                                                      index,
                                                      visibleSecretIndexMap[
                                                        secretIndex
                                                      ] as number,
                                                    )
                                                  }
                                                  aria-label={t('removeSecret')}
                                                >
                                                  <Delete01Icon size={16} />
                                                </IconButton>
                                              </HStack>
                                              {errors[index]?.secrets?.[
                                                visibleSecretIndexMap[
                                                  secretIndex
                                                ] as number
                                              ] && (
                                                <Text
                                                  fontSize="sm"
                                                  color="fg.error"
                                                  ml={2}
                                                >
                                                  {
                                                    errors[index].secrets?.[
                                                      visibleSecretIndexMap[
                                                        secretIndex
                                                      ] as number
                                                    ]
                                                  }
                                                </Text>
                                              )}
                                            </VStack>
                                          ),
                                        )}
                                      </VStack>
                                    )}
                                  </VStack>
                                  <Divider />
                                  <VStack gap={2} alignItems="stretch">
                                    <Text fontSize="sm" fontWeight="medium">
                                      {t('bodySignature.title')}
                                    </Text>
                                    <Text fontSize="xs" color="fg.muted">
                                      {t('bodySignature.description')}
                                    </Text>
                                    <HStack
                                      gap={2}
                                      alignItems="center"
                                      w="full"
                                      flexWrap="nowrap"
                                    >
                                      <Box flex={1} minW={0}>
                                        <Input
                                          placeholder={t(
                                            'bodySignature.headerNamePlaceholder',
                                          )}
                                          value={
                                            credential.bodySignature
                                              ?.headerName ?? ''
                                          }
                                          onChange={(e) =>
                                            handleBodySignatureChange({
                                              index,
                                              field: 'headerName',
                                              value: e.target.value,
                                            })
                                          }
                                          w="full"
                                        />
                                      </Box>
                                      <Box flex={1} minW={0}>
                                        <SensitiveInput
                                          value={
                                            credential.bodySignature?.secret ??
                                            ''
                                          }
                                          placeholder={t(
                                            'bodySignature.secretPlaceholder',
                                          )}
                                          onChange={(value) =>
                                            handleBodySignatureChange({
                                              index,
                                              field: 'secret',
                                              value,
                                            })
                                          }
                                          fieldKey={`${index}-body-signature-secret-key`}
                                          allowViewingCredentials={
                                            allowViewingCredentials
                                          }
                                          onCopy={onCopy}
                                          copiedState={
                                            copiedCredentials[
                                              `${index}-body-signature-secret-key`
                                            ] || false
                                          }
                                          visibleState={
                                            visibleCredentials[
                                              `${index}-body-signature-secret-key`
                                            ] || false
                                          }
                                          onToggleVisibility={
                                            toggleCredentialVisibility
                                          }
                                          onCopySuccess={() =>
                                            handleCopySuccess(
                                              `${index}-body-signature-secret-key`,
                                            )
                                          }
                                          type="password"
                                          isDefaultValue={isDefaultSecretValue(
                                            credential.bodySignature?.secret,
                                          )}
                                        />
                                      </Box>
                                      <Box flexShrink={0} w="132px">
                                        <Select.Root
                                          positioning={{ sameWidth: true }}
                                          value={[
                                            credential.bodySignature
                                              ?.algorithm ?? 'sha256',
                                          ]}
                                          collection={
                                            bodySignatureAlgorithmOptions
                                          }
                                          onValueChange={(e) =>
                                            handleBodySignatureChange({
                                              index,
                                              field: 'algorithm',
                                              value: e.value[0] ?? 'sha256',
                                            })
                                          }
                                        >
                                          <Select.Control w="full" minW="132px">
                                            <Select.Trigger>
                                              <Select.ValueText />
                                              <ArrowUpDownIcon size={16} />
                                            </Select.Trigger>
                                          </Select.Control>
                                          <Select.Positioner>
                                            <Select.Content>
                                              <Select.ItemGroup>
                                                {bodySignatureAlgorithmOptions.items.map(
                                                  (option) => (
                                                    <Select.Item
                                                      key={option.value}
                                                      item={option}
                                                    >
                                                      <Select.ItemText>
                                                        {option.label}
                                                      </Select.ItemText>
                                                      <Select.ItemIndicator>
                                                        <Tick01Icon size={16} />
                                                      </Select.ItemIndicator>
                                                    </Select.Item>
                                                  ),
                                                )}
                                              </Select.ItemGroup>
                                            </Select.Content>
                                          </Select.Positioner>
                                        </Select.Root>
                                      </Box>
                                    </HStack>
                                    {errors[index]?.bodySignature && (
                                      <Text fontSize="sm" color="fg.error">
                                        {errors[index].bodySignature}
                                      </Text>
                                    )}
                                  </VStack>
                                </VStack>
                              </Box>
                            </Collapsible.Content>
                          </Collapsible.Root>
                        </VStack>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </VStack>
        )}
        <ConfirmationDialog
          isOpen={credentialToToggle !== null}
          onOpenChange={({ open }) => {
            if (!open) {
              setCredentialToToggle(null);
            }
          }}
          onConfirm={confirmToggleCredentialStatus}
          variant={
            credentialToToggle?.currentStatus ? 'default' : 'destructive'
          }
          title={
            credentialToToggle?.currentStatus
              ? t('toggleStatus.enableConfirmTitle')
              : t('toggleStatus.disableConfirmTitle')
          }
          description={
            credentialToToggle?.currentStatus
              ? t('toggleStatus.enableConfirmDescription')
              : t('toggleStatus.disableConfirmDescription')
          }
          confirmText={
            credentialToToggle?.currentStatus
              ? t('toggleStatus.enableConfirm')
              : t('toggleStatus.disableConfirm')
          }
          cancelText={t('toggleStatus.cancel')}
          loading={isTogglingStatus}
        />
        {onSSESubscribe &&
          credentials
            .filter((credential) => {
              return (
                credential.id &&
                (testingById[credential.id] ||
                  isCredentialVerificationInProgress({
                    status: credential.verification?.status,
                  })) &&
                streamingIdById[credential.id]
              );
            })
            .map((credential) => {
              const credentialId = credential.id;
              const streamingId = streamingIdById[credentialId];

              return (
                <AiBrowserStreamingFrame
                  key={credentialId}
                  streamingId={streamingId}
                  modalOnly={true}
                  isModalOpen={openModalCredentialId === credentialId}
                  onModalOpenChange={(open) => {
                    if (!open) {
                      setOpenModalCredentialId(null);
                    } else {
                      setOpenModalCredentialId(credentialId);
                    }
                  }}
                  onSSESubscribe={onSSESubscribe}
                />
              );
            })}
      </VStack>
    );
  },
);
ApplicationCredentials.displayName = 'ApplicationCredentials';

export { CredentialsSkeleton } from './CredentialsSkeleton';
export type { GatheredArtifactItem } from './GatheredArtifactsPanel';
export {
  GatheredArtifactsPanel,
  GatheredArtifactsTooltipContent,
} from './GatheredArtifactsPanel';
export type { FetchCredentialInboxFn } from './CredentialInboxMessagesPanel';
export {
  CredentialInboxMessagesPanel,
  credentialHasResolvableMailbox,
  resolveGeneratedMailboxAddress,
} from './CredentialInboxMessagesPanel';
export { GeneratedMailboxSection } from './GeneratedMailboxSection';
